import React from "react";
import cx from "classnames";
import Repeater from "Components/repeater/Repeater";
import Button from "Components/button/Button";
import Checkbox from "Components/checkbox/Checkbox";
import TranslatedString from "Components/TranslatedString";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { isValidIPv4CIDR } from "Utils/ip";
import Toggle from "Components/toggle/Toggle";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import Select from "Components/select/Select";
import t from "Utils/translation";
import s from "./securityDomains.scss";
import DKIM from "./DKIM.jsx";
import QboxmailApi from "../../../qboxmailApi";
import { alertSuccess } from "Utils/alerts";
import RadioButton from "Components/radiobutton/RadioButton";
import AvailabilityBox from "Components/availabilityBox/AvailabilityBox";
import BetaLabel from "Components/betaLabel/BetaLabel";

export default class SecurityForm extends React.Component {
  state = {
    antispamLevelOptions: [
      {
        value: 3,
        label: t("input.label.antispam.level.aggressive"),
      },
      {
        value: 2,
        label: t("input.label.antispam.level.standard"),
      },
      {
        value: 1,
        label: t("input.label.antispam.level.tolerant"),
      },
      {
        value: 0,
        label: t("input.label.antispam.level.absent"),
      },
    ],
    viewFields: [
      "api_enabled",
      "force_otp",
      "allow_nets_apply_to_all",
      "allow_nets",
      "password_change_disabled",
      "password_recovery_disabled",
      "password_expiration",
      "force_password_first_login",
      "block_old_password",
      "password_policies_apply_to_all",
      "antispam_level",
      "destroy_sessions_on_password_expiration",
      "antimalware_premium",
      "limit_tracemail_21_days",
    ],
    forcingResetPassword: false,
    resetPasswordForced: false,
  };

  componentWillUnmount() {
    this.props.resetFields(...this.state.viewFields);
  }

  forceResetPassword = () => {
    this.setState({ forcingResetPassword: true });
    const api = new QboxmailApi({ useCookie: true });
    return api
      .editDomain(this.props.domain.code, { force_new_password: true })
      .then(() => {
        this.setState({ forcingResetPassword: false, resetPasswordForced: true });
        alertSuccess(t("alert.domainUpdateSuccess"));
      })
      .catch(() => {
        this.setState({ forcingResetPassword: false });
      });
  };

  render() {
    const {
      formData,
      setFormFieldValue,
      formErrors,
      isFormLoading,
      submitForm,
      domain,
      domain: {
        plan: { features },
      },
    } = this.props;

    return (
      <form className={s.security} method="post" onSubmit={(e) => submitForm(e, this.state.viewFields)}>
        <div className={s.wrapInfoBtn}>
          <div className={s.warnignMsg}>
            {!this.props.areFieldsPristine(...this.state.viewFields) && (
              <WarningBanner color="yellow-text" msg={<TranslatedString>infoMsg.unsaveChanged</TranslatedString>} />
            )}
          </div>
          <div className={s.infoBtnGroup}>
            <Button docsKey="domains_settings" color="docs" type="button">
              button.docs
            </Button>
            <Button type="submit" loading={isFormLoading}>
              button.save
            </Button>
          </div>
        </div>
        <div className={s.settings}>
          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="flex items-center gap-2 settingTitle">
                {t("settings.sections.tracemail21DaysLimit.title")}
              </div>
              <div className="sectionDesc">{t("settings.sections.tracemail21DaysLimit.desc")}</div>
            </div>
            <div className="formsSectionSP">
              <Toggle
                className="inputFull2"
                label={t("input.label.tracemail21DaysLimit")}
                name="limit_tracemail_21_days"
                checked={formData.limit_tracemail_21_days}
                errors={formErrors.limit_tracemail_21_days}
                onChange={setFormFieldValue}
              />
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="flex items-center gap-2 settingTitle">
                {t("settings.sections.antimalwarePremium.title")}

                <BetaLabel fullyRounded />
              </div>
              <div className="sectionDesc">{t("settings.sections.antimalwarePremium.desc")}</div>
            </div>
            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic"]}>
                <Toggle
                  className="inputFull2"
                  label={t("input.label.antimalware_premium")}
                  name="antimalware_premium"
                  checked={formData.antimalware_premium}
                  errors={formErrors.antimalware_premium}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name == "basic"}
                />
              </AvailabilityBox>
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.password.title")}</div>
              <div className="sectionDesc">{t("settings.sections.password.desc.domain")}</div>
            </div>
            <div className="formsSectionSP">
              <Toggle
                className="inputFull2"
                label={t("input.label.force_password_first_login")}
                name="force_password_first_login"
                checked={formData.force_password_first_login}
                errors={formErrors.force_password_first_login}
                onChange={setFormFieldValue}
              />

              <Toggle
                className="inputFull2"
                label={t("input.label.disable_pwd_change")}
                name="password_change_disabled"
                checked={formData.password_change_disabled}
                errors={formErrors.password_change_disabled}
                onChange={setFormFieldValue}
              />

              <Toggle
                className="inputFull2"
                label={t("input.label.disable_pwd_recovery")}
                name="password_recovery_disabled"
                checked={formData.password_recovery_disabled}
                errors={formErrors.password_recovery_disabled}
                onChange={setFormFieldValue}
              />

              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic", "professional"]}>
                <Toggle
                  className="inputFull2"
                  label={t("input.label.block_old_pwd")}
                  name="block_old_password"
                  checked={formData.block_old_password}
                  errors={formErrors.block_old_password}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name !== "enterprise"}
                />

                <div className="inputFull2 wrapInlineBtnSP">
                  <div className={cx("labelInlineBtnSP", { disabled: domain.plan.name !== "enterprise" })}>
                    {t("input.label.password.forceChange.nextLogin.domain")}
                  </div>
                  <Button
                    color="outline"
                    type="button"
                    loading={this.state.forcingResetPassword}
                    onClick={this.forceResetPassword}
                    disabled={domain.plan.name !== "enterprise"}
                  >
                    button.force.change
                  </Button>
                </div>

                {this.state.resetPasswordForced && (
                  <div className="inputFull2">
                    <WarningBanner
                      color="blue"
                      msg={<TranslatedString>infoMsg.resetPwdForcedAll</TranslatedString>}
                      info
                    />
                  </div>
                )}
              </AvailabilityBox>

              <Checkbox
                className="inputFull2"
                label={<TranslatedString>input.label.applyPwdPoliciesToAll</TranslatedString>}
                name="password_policies_apply_to_all"
                value={formData.password_policies_apply_to_all}
                errors={formErrors.password_policies_apply_to_all}
                onChange={setFormFieldValue}
              />
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.passwordExpiration.title")}</div>
              <div className="sectionDesc">{t("settings.sections.passwordExpiration.desc")}</div>
            </div>
            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic"]}>
                <Select
                  className={cx("inputFull2", "selectExpiration")}
                  label={t("input.label.pwd_expiration")}
                  components={{ DropdownIndicator }}
                  name="password_expiration"
                  onChange={setFormFieldValue}
                  value={parseInt(formData.password_expiration)}
                  errors={formErrors.password_expiration}
                  options={[
                    {
                      label: t("input.label.pwd_expiration.never"),
                      value: -1,
                    },
                    {
                      label: t("input.label.pwd_expiration.3"),
                      value: 3,
                    },
                    {
                      label: t("input.label.pwd_expiration.6"),
                      value: 6,
                    },
                    {
                      label: t("input.label.pwd_expiration.12"),
                      value: 12,
                    },
                  ]}
                  clearable={false}
                  inline={true}
                  disabled={domain.plan.name === "basic"}
                />

                {formData.password_expiration > 0 && (
                  <p className={cx("inputFull2", "sectionDesc")}>
                    {t("input.label.password_expiration_counter_start", null, { months: formData.password_expiration })}
                  </p>
                )}

                <Toggle
                  className="inputFull2"
                  label={t("input.label.destroy_sessions_on_password_expiration")}
                  name="destroy_sessions_on_password_expiration"
                  checked={formData.destroy_sessions_on_password_expiration}
                  errors={formErrors.destroy_sessions_on_password_expiration}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name === "basic"}
                />

                <Checkbox
                  className="inputFull2"
                  label={<TranslatedString>input.label.applyPwdPoliciesToAll</TranslatedString>}
                  name="password_policies_apply_to_all"
                  value={formData.password_policies_apply_to_all}
                  errors={formErrors.password_policies_apply_to_all}
                  onChange={setFormFieldValue}
                />
              </AvailabilityBox>
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.otp.title")}</div>
              <div className="sectionDesc">{t("settings.sections.otp.desc.domain")}</div>
            </div>
            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic", "professional"]}>
                <Toggle
                  className="inputFull2"
                  label={t("input.label.force_otp")}
                  name="force_otp"
                  checked={formData.force_otp}
                  errors={formErrors.force_otp}
                  onChange={setFormFieldValue}
                  disabled={domain.plan.name !== "enterprise"}
                />
              </AvailabilityBox>
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.antispam.title")}</div>
              <div className="sectionDesc">{t("settings.sections.antispam.desc.domain")}</div>
            </div>
            <div className="formsSectionSP">
              <RadioButton
                className="inputFull2"
                children={this.state.antispamLevelOptions}
                name="antispam_level"
                value={parseInt(formData.antispam_level)}
                onChange={setFormFieldValue}
              />
            </div>
          </div>

          <DKIM {...this.props} />

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="flex items-center gap-2 settingTitle">{t("settings.sections.ipEnabled.title")}</div>
              <div className="sectionDesc">
                <TranslatedString>settings.sections.ipEnabled.desc.domain</TranslatedString>
              </div>
            </div>
            <div className="formsSectionSP">
              <Repeater
                className="inputFull2"
                labelInput={t("input.label.add.ipAddress")}
                label={t("listTable.header.title.ipEnabled")}
                values={formData.allow_nets}
                onChange={setFormFieldValue}
                maxValues={5}
                name="allow_nets"
                errors={formErrors.allow_nets}
                placeholder={t("input.placeholder.ip")}
                validateNewValue={(newVal) => {
                  if (!isValidIPv4CIDR(newVal)) {
                    alert(`${newVal} + ${t("errorMsg.notValid.ip")}`);
                    return false;
                  }
                  return true;
                }}
              />
              <Checkbox
                className="inputFull2"
                label={t("input.label.ip.restrictionsToAll")}
                name="allow_nets_apply_to_all"
                onChange={setFormFieldValue}
                checked={formData.allow_nets_apply_to_all}
                errors={formErrors.allow_nets_apply_to_all}
              />
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.api.title")}</div>
              <div className="sectionDesc">{t("settings.sections.api.desc.domain")}</div>
            </div>
            <div className="formsSectionSP">
              <AvailabilityBox currentPlan={domain.plan.name} notAvailableFor={["basic"]}>
                <Toggle
                  className="inputFull2"
                  label={t("input.label.api_enabled")}
                  name="api_enabled"
                  checked={!!formData.api_enabled}
                  errors={formErrors.api_enabled}
                  onChange={setFormFieldValue}
                  disabled={!features.api_enabled}
                />
              </AvailabilityBox>
            </div>
          </div>
        </div>
      </form>
    );
  }
}
